import { Fragment, useMemo } from 'react';
import { KoobMap, KoobMapClusterer } from '@koob/maps';
import { sortByCompatibility } from '@koob/utils';
import { getExperiencePoints } from '../../logic/mapPoints';
import { useDays, useSearchHotels } from '../../hooks/useTripStoreSelectors';
import type { TripMapState } from '../../hooks/useTripMapState';
import TripMapHotelMarkers from './TripMapHotelMarkers';
import TripMapItinerary from './TripMapItinerary';
import type {
  TripMapHotelCardProps,
  TripMapHotels,
  TripMapMarkers,
} from './types';

// absolute map: the card only has a min-height, so percentage heights inside
// the flex item resolve to 0
const MAP_STYLE = { position: 'absolute', inset: 0 } as const;

interface TripMapProps {
  // What `useTripMapState` returns; the screen calls it so the map keeps its
  // camera when a tab hides it.
  mapState: TripMapState;
  // The experiences of the list; the map draws the ones that have a city.
  experiences: any[];
  // Best matches first, so a group of pins opens on the one the list shows
  // first.
  getExperienceCompatibility: (experience: any) => string | null;
  hotels: TripMapHotels;
  // The route of the trip instead of the products of the list.
  itineraryMode?: boolean;
  markers: TripMapMarkers;
  hotelCardProps: TripMapHotelCardProps;
  onAddHotel: (hotel: any) => void;
}

// The map: pins for the list, or the itinerary of the trip. The app's
// `KoobMapsProvider` loads Google.
export default function TripMap({
  mapState,
  experiences,
  getExperienceCompatibility,
  hotels,
  itineraryMode = false,
  markers,
  hotelCardProps,
  onAddHotel,
}: TripMapProps) {
  const days = useDays();
  const searchHotels = useSearchHotels();
  const points = useMemo(
    () =>
      sortByCompatibility(
        getExperiencePoints(experiences),
        getExperienceCompatibility,
      ),
    [experiences, getExperienceCompatibility],
  );

  return (
    <div className="relative min-h-0 flex-1">
      <KoobMap
        style={MAP_STYLE}
        center={mapState.center}
        zoom={mapState.zoom}
        onLoad={mapState.onLoad}
        onUnmount={mapState.onUnmount}
        onZoomChanged={mapState.onZoomChanged}
        onBoundsChanged={mapState.onBoundsChanged}
      >
        {itineraryMode ? (
          <TripMapItinerary
            days={days}
            showHotels={searchHotels}
            markers={markers}
            hotelCardProps={hotelCardProps}
          />
        ) : (
          <KoobMapClusterer>
            {!searchHotels &&
              points.map((experience) => (
                <Fragment key={'map-e-' + experience?.id}>
                  {markers.renderExperienceMarker(experience)}
                </Fragment>
              ))}

            <TripMapHotelMarkers
              hotels={hotels}
              showAvailable={searchHotels}
              hotelCardProps={hotelCardProps}
              onAddHotel={onAddHotel}
            />
          </KoobMapClusterer>
        )}
      </KoobMap>
    </div>
  );
}
